/**
 * The app's share URLs, resolved on the web: `/sticker/<id>`, `/u/<id>` and
 * `/@<username>`.
 *
 * The app opens these itself when it is installed (the AASA file claims every
 * path), so the website only sees them without the app, on desktop, or when a
 * link is pasted into a browser. They 404ed until 2026-10: no route existed
 * for any of them.
 *
 * Pure and dependency-free so the vitest suite can pin the parsing rules, and
 * so proxy.ts can import it.
 */

/**
 * A database id as it appears in a URL, or null. Ids are MySQL auto-increment
 * ints; anything else — "abc", "0", "01", "1.5", a 40-digit number — is
 * rejected here so it never reaches the API as a request it cannot answer.
 */
export function parseNumericId(raw: string): string | null {
  return /^[1-9][0-9]{0,11}$/.test(raw) ? raw : null;
}

/** The creator profile for a pack's author, or null when there is no author. */
export function creatorPath(userId: number | string | null | undefined): string | null {
  const id = parseNumericId(String(userId ?? ""));
  return id ? `/u/${id}` : null;
}

/** The fragment the pack page gives each sticker, for `/sticker/<id>` to land on. */
export function stickerAnchorId(stickerId: number | string): string {
  return `sticker-${stickerId}`;
}

/**
 * The username in a `/@<name>` segment, or null when it cannot be one.
 *
 * Decoded but NOT lowercased: the API's lookup is case-insensitive already, and
 * usernames from older signups keep mixed case. A leading "@" is dropped, which
 * covers `/%40name` (the encoded spelling some share sheets produce, which
 * misses the `/@:name` rewrite) and a doubled `/@@name`. Usernames never
 * contain whitespace or a slash (sticko-api-v2 src/modules/auth/username.ts),
 * and the column is varchar(190) at most.
 */
export function parseUsername(raw: string): string | null {
  let name: string;
  try {
    name = decodeURIComponent(raw);
  } catch {
    return null;
  }
  name = name.replace(/^@+/, "").trim();
  if (!name || name.length > 190 || /[\s/]/u.test(name)) return null;
  return name;
}

/** A profile's display name: "First Last", falling back to the username. */
export function creatorDisplayName(user: {
  firstName: string | null;
  lastName: string | null;
  username: string;
}): string {
  const full = [user.firstName, user.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  return full || user.username;
}

/**
 * The raw handle segment of the app's `/@<name>` share URL, or null.
 *
 * No route folder can express this URL — `app/@name` is a parallel-route slot,
 * not a segment — so proxy.ts rewrites it onto app/u/by-name/[name], which 307s
 * to the canonical /u/<id>. The proxy rather than a next.config rewrite,
 * because a `source` pattern only matches a literal "@", and share sheets also
 * send the percent-encoded `/%40name`. The segment is passed on still encoded;
 * the route decodes it with parseUsername above.
 */
export function handleSegment(pathname: string): string | null {
  const match = /^\/(?:@|%40)([^/]+)$/i.exec(pathname);
  return match ? match[1]! : null;
}
